var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
const PROBE_DELAYS_MS = [5000, 10000, 20000, 30000];
const RESTORED_MS = 3000;
const PROBE_TIMEOUT_MS = 8000;
let state = 'online';
let probeUrl = '';
let probeAttempt = 0;
let probeTimer;
let restoredTimer;
let probing = false;
const listeners = new Set();
function set(next) {
    if (next === state)
        return;
    state = next;
    listeners.forEach((l) => l(state));
}
function isDown() {
    return state === 'offline' || state === 'unreachable' || state === 'checking';
}
function clearProbe() {
    window.clearTimeout(probeTimer);
    probeTimer = undefined;
    probeAttempt = 0;
}
function scheduleProbe() {
    if (!listeners.size || probeTimer !== undefined)
        return;
    const delay = PROBE_DELAYS_MS[Math.min(probeAttempt, PROBE_DELAYS_MS.length - 1)];
    probeAttempt += 1;
    probeTimer = window.setTimeout(() => {
        probeTimer = undefined;
        void probe();
    }, delay);
}
/** Any response at all, even an error status, means the server is reachable. */
function probe() {
    return __awaiter(this, void 0, void 0, function* () {
        if (probing)
            return;
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            set('offline');
            return;
        }
        const url = probeUrl || process.env.REACT_APP_VRITI_BASE_URL || '';
        if (!url)
            return;
        probing = true;
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
        try {
            // no-cors: an opaque response still proves the host answered; no auth header is sent.
            yield fetch(url, {
                method: 'GET',
                mode: 'no-cors',
                cache: 'no-store',
                signal: controller.signal,
            });
            reportServerReachable();
        }
        catch (_a) {
            set('unreachable');
            scheduleProbe();
        }
        finally {
            window.clearTimeout(timeout);
            probing = false;
        }
    });
}
export function setConnectionProbeUrl(url) {
    probeUrl = url;
}
export function reportServerReachable() {
    if (!isDown())
        return;
    clearProbe();
    set('restored');
    window.clearTimeout(restoredTimer);
    restoredTimer = window.setTimeout(() => {
        if (state === 'restored')
            set('online');
    }, RESTORED_MS);
}
export function reportServerUnreachable() {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        set('offline');
        return;
    }
    if (state === 'unreachable')
        return;
    window.clearTimeout(restoredTimer);
    set('unreachable');
    scheduleProbe();
}
function onOffline() {
    clearProbe();
    window.clearTimeout(restoredTimer);
    set('offline');
}
function onOnline() {
    set('checking');
    clearProbe();
    void probe();
}
export function getConnectionState() {
    return state;
}
/** Subscribe to changes. Window listeners are attached only while someone is subscribed. */
export function subscribeConnection(listener) {
    if (!listeners.size) {
        window.addEventListener('offline', onOffline);
        window.addEventListener('online', onOnline);
        if (navigator.onLine === false)
            set('offline');
        else if (isDown())
            scheduleProbe();
    }
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
        if (!listeners.size) {
            window.removeEventListener('offline', onOffline);
            window.removeEventListener('online', onOnline);
            clearProbe();
        }
    };
}
